import React, { useId, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { CalendarDays, ChevronDown, Download, Minus, TrendingDown, TrendingUp } from 'lucide-react';
import { FormError } from '../components';
import { LeerZeile } from '../../pages/einstellungen/Bausteine';
import { ErklaerFenster, type ErklaerInhalt } from '../../components/Erklaerung';
import { InfoTipp } from '../../components/Erklaerung';
import { STIL, TITEL, ZEITRAUM } from './cockpitTexte';
import Diagramm, { diagrammSatz, type DiagrammReihe } from './Diagramm';
import type { Einheit } from './cockpitApi';
import { trendText, type Trend } from './format';
import { angezeigtText, heuteInWien, wahlName, Zeitraum, ZeitraumWahl as Wahl } from './zeitraum';
import ZeitraumKalender from './ZeitraumKalender';
import './cockpit.css';

// Profil-Nachbau F-07, F-08 (output/profil-team-20261014/BAU-COCKPIT.md 1.1): die Bausteine des KI-Cockpits für beide Ebenen –
// Kopf, Reiterleiste, Zeitraum, Kennzahlenleiste mit offengelegter Regel, Säulen ohne Diagrammbibliothek, Tabelle und
// Exportzeile. Sie zeigen nur, was sie bekommen: Beträge formatiert der Aufrufer (Plattform über netMoney), die Bausteine selbst
// kennen kein Euro. Maße in cockpit.css (GESTALTUNG.md 2.4, 2.11, 2.13, 2.17). Seit F-25 (PF-GR-cockpit-2) ist der Zeitraum ein Knopf
// mit Kalender (ZeitraumKalender.tsx, kalender.css).

/** Kopf der Seite: Titel „KI-Cockpit“ und die Beschreibung der Ebene. Die Überschrift nimmt den Fokus beim Öffnen einer
 *  Bereichsseite an (tabIndex -1, BereichsSeite.tsx, F-19). F-24: erklaerung setzt den Link „Wie funktioniert …?“ rechts in den Kopf. */
export function CockpitKopf({ beschreibung, aktion, erklaerung }: { beschreibung: string; aktion?: React.ReactNode; erklaerung?: ErklaerInhalt }) {
  return <header className="ki-cockpit-kopf">
    <div><h1 tabIndex={-1}>{TITEL}</h1><p>{beschreibung}</p></div>
    {(aktion || erklaerung) && <div className="ki-cockpit-kopf-aktion">{erklaerung && <ErklaerFenster {...erklaerung} />}{aktion}</div>}
  </header>;
}

export type ReiterEintrag<T extends string> = { key: T; titel: string };
/** Reiterleiste als Links: ein Wechsel ändert nur den Teil in der Adresse (ziel), der Zeitraum bleibt. */
export function Reiter<T extends string>({ name, reiter, aktiv, ziel }: { name: string; reiter: ReiterEintrag<T>[]; aktiv: T; ziel: (key: T) => string }) {
  return <nav className="ki-reiter" aria-label={name}><ul>
    {reiter.map(eintrag => <li key={eintrag.key}>
      <Link to={ziel(eintrag.key)} aria-current={eintrag.key === aktiv ? 'page' : undefined}>{eintrag.titel}</Link>
    </li>)}
  </ul></nav>;
}

/** Zeitraum für alle Teile (F-08), seit F-25 (PF-GR-cockpit-2, SPEC cockpit-grafiken 2.3) ein Knopf mit Kalender wie meinGPT
 *  (ZeitraumKalender.tsx) statt der Auswahlliste. filter steht in derselben Zeile hinter dem Knopf (Team, Tarif, Quelle), dahinter
 *  „Angezeigt: …“ aus der Antwort des Servers (angezeigt), sobald sie da ist. Schließen per Escape, „Abbrechen“, Vorgabe oder
 *  „Anwenden“ gibt den Fokus an den Knopf zurück. */
export function ZeitraumWahl({ wahl, angezeigt, onWahl, filter }: { wahl: Wahl; angezeigt?: Zeitraum; onWahl: (wahl: Wahl) => void;
  filter?: React.ReactNode }) {
  const [offen, setOffen] = useState(false);
  const knopf = useRef<HTMLButtonElement>(null);
  const id = useId();
  const schliessen = (zurueck: boolean) => { setOffen(false); if (zurueck) knopf.current?.focus(); };
  return <div className="ki-zeitraum">
    <div className="ki-werkzeuge" role="group" aria-label={ZEITRAUM.werkzeuge}>
      <button ref={knopf} type="button" className="adm-button ki-zeitraum-knopf" aria-haspopup="dialog" aria-expanded={offen}
        aria-controls={offen ? `${id}-kalender` : undefined} onClick={() => setOffen(vorher => !vorher)}
        onKeyDown={event => { if (event.key === 'ArrowDown' && !offen) { event.preventDefault(); setOffen(true); } }}>
        <CalendarDays size={18} aria-hidden="true" /><span className="adm-sr-only">{ZEITRAUM.feld}: </span><span>{wahlName(wahl)}</span>
        <ChevronDown className="ki-zeitraum-pfeil" size={16} aria-hidden="true" />
      </button>
      {filter}
      {angezeigt && <p className="ki-zeitraum-info">{angezeigtText(angezeigt)}</p>}
    </div>
    {offen && <ZeitraumKalender id={`${id}-kalender`} wahl={wahl} angezeigt={angezeigt} knopf={knopf}
      onWahl={neu => { schliessen(true); onWahl(neu); }} onSchliessen={schliessen} />}
  </div>;
}

export type KennzahlEintrag = { key: string; titel: string; wert: React.ReactNode; zusatz?: React.ReactNode; aktion?: React.ReactNode; tipp?: React.ReactNode;
  vergleich?: Trend; ton?: 'negativ' };
/** Kennzahlen als eine Leiste (Bezeichnung, Wert, Zusatz).
 *  F-24: tipp setzt das Info-Symbol hinter den Titel der Kachel (Regel der Kachel, F-07); der Aufklapper „So rechnet CLAiRIS“
 *  darunter entfällt (PF-TX-cockpit-2).
 *  F-25 (PF-GC-kennzahlen-1): vergleich setzt unter den Wert die Unterzeile zum Vorzeitraum, Pfeil und Text, nie nur Farbe
 *  (TrendingUp, TrendingDown, Minus; Wörter von STIL wie Vergleich in BerichtBausteine.tsx: „+6 % zum Vorzeitraum“, „neu, davor
 *  keine“). Ohne vergleich bleibt das Markup wie bisher.
 *  F-25 (PF-GC-kosten-1): ton 'negativ' zeigt den Zusatz in der Signalfarbe (negative Marge; das Minuszeichen steht im Text). */
export function KennzahlLeiste({ label, kennzahlen }: { label: string; kennzahlen: KennzahlEintrag[] }) {
  const vergleich = (trend: Trend) => {
    const Pfeil = trend.art === 'NEU' || (trend.art === 'PROZENT' && trend.prozent > 0) ? TrendingUp
      : trend.art === 'PROZENT' && trend.prozent < 0 ? TrendingDown : Minus;
    return <small className="ki-kennzahl-vergleich"><Pfeil size={16} aria-hidden="true" />{trend.art === 'NEU' ? STIL.neu : `${trendText(trend)}${STIL.zumVorzeitraum}`}</small>;
  };
  return <div className="ki-kennzahlen-block">
    <div className="ki-kennzahlen" role="group" aria-label={label}>
      {kennzahlen.map(kennzahl => <div key={kennzahl.key} className="ki-kennzahl">
        {kennzahl.tipp ? <span className="ki-kennzahl-kopf"><span className="ki-kennzahl-titel">{kennzahl.titel}</span><InfoTipp wort={kennzahl.titel} text={kennzahl.tipp} /></span> : <span className="ki-kennzahl-titel">{kennzahl.titel}</span>}
        <strong className="ki-kennzahl-wert">{kennzahl.wert}</strong>
        {kennzahl.zusatz && <small className={kennzahl.ton === 'negativ' ? 'ki-kennzahl-negativ' : undefined}>{kennzahl.zusatz}</small>}
        {kennzahl.vergleich && vergleich(kennzahl.vergleich)}
        {kennzahl.aktion && <div className="ki-kennzahl-aktion">{kennzahl.aktion}</div>}
      </div>)}
    </div>
  </div>;
}

export type SaeulenAbschnitt = { von: string; bis: string; werte: number[] };
/** Säulen des Teils „Verlauf“ (bis F-25 eigene Elemente mit title). Profil-Nachbau F-25 (PF-GR-cockpit-1): zeichnet über den
 *  Baustein Diagramm (SVG, Achsen, Gitter, Tooltip per Maus, Finger und Tastatur); Titel, Legende und Angaben bleiben. */
export function Saeulen({ titel, einheit, abschnitte, teile, wertText }: { titel: string; einheit: Einheit; abschnitte: SaeulenAbschnitt[];
  teile?: readonly string[]; wertText: (werte: number[]) => string }) {
  const reihen: DiagrammReihe[] = (teile && teile.length > 1 ? teile : [titel]).map((name, i) => ({ name, farbe: i === 0 ? 'recherchen' : 'nachfragen' }));
  const einzeln = !teile || teile.length < 2;
  return <figure className="ki-saeulen">
    <figcaption>{titel}</figcaption>
    {!einzeln && <ul className="ki-legende">{teile!.map((teil, i) => <li key={teil}><span className={`ki-farbe ki-farbe-${i}`} aria-hidden="true" />{teil}</li>)}</ul>}
    <Diagramm art="saeulen" name={titel} einheit={einheit} abschnitte={abschnitte} reihen={reihen} heute={heuteInWien(new Date())}
      {...(einzeln ? { wertText: (wert: number) => wertText([wert]) } : {})} vorlesen={diagrammSatz(titel, einheit, abschnitte)} />
  </figure>;
}

export type Spalte = { key: string; titel: string; zahl?: boolean };
export type TabellenZeile = { key: string; zellen: React.ReactNode[] };
/** Tabelle nach GESTALTUNG.md 2.11: Zahlen rechtsbündig mit tabular-nums, unter 800 px je Zeile ein Block mit dem Spaltentitel
 *  über dem Wert (einst-tabelle-schmal, data-spalte, GB-05); leer eine ruhige Zeile (LeerZeile), Summen im Fuß. */
export function WerteTabelle({ label, spalten, zeilen, fuss, leer }: { label: string; spalten: Spalte[]; zeilen: TabellenZeile[]; fuss?: React.ReactNode[]; leer: string }) {
  const zelle = (inhalt: React.ReactNode, i: number) => <td key={spalten[i].key} data-spalte={spalten[i].titel} className={spalten[i].zahl ? 'ki-zahl' : undefined}>{inhalt}</td>;
  return <div className="adm-table-scroll ki-tabelle-rahmen" role="region" aria-label={label} tabIndex={0}>
    <table className="ki-tabelle einst-tabelle-schmal">
      <thead><tr>{spalten.map(spalte => <th key={spalte.key} scope="col" className={spalte.zahl ? 'ki-zahl' : undefined}>{spalte.titel}</th>)}</tr></thead>
      <tbody>{zeilen.length ? zeilen.map(zeile => <tr key={zeile.key}>{zeile.zellen.map(zelle)}</tr>) : <LeerZeile spalten={spalten.length}>{leer}</LeerZeile>}</tbody>
      {fuss && zeilen.length > 0 && <tfoot><tr>{fuss.map(zelle)}</tr></tfoot>}
    </table>
  </div>;
}

/** Eine Zeile des Reiters „Export“: Inhalt und Satz links, der Knopf rechts; Meldung und Fehler am Knopf. Alle Knöpfe heißen
 *  gleich; welchen Inhalt einer lädt, sagt ihnen der Titel der Zeile (aria-describedby). F-24 (PF-TX-cockpit-2): satz wahlweise;
 *  das Plattform-Cockpit nennt ihn einmal über allen Zeilen. */
export function ExportZeile({ titel, satz, knopf, busy, onLaden, meldung, fehler }: { titel: string; satz?: string; knopf: string; busy: boolean;
  onLaden: () => void; meldung?: string; fehler?: string }) {
  const id = useId();
  return <div className="ki-export-zeile">
    <div className="ki-export-text"><strong id={`${id}-titel`}>{titel}</strong>{satz && <p>{satz}</p>}</div>
    <div className="ki-export-aktion">
      <button type="button" className="adm-button" disabled={busy} aria-busy={busy || undefined} aria-describedby={`${id}-titel`} onClick={onLaden}>
        <Download size={16} aria-hidden="true" />{knopf}</button>
      {meldung && <p className="adm-success" role="status">{meldung}</p>}
      <FormError message={fehler} />
    </div>
  </div>;
}
